import { ChangeEvent, useCallback, useEffect, useRef, useState, MouseEventHandler } from 'react';
import type { AddressSearchResult } from '@domgen/base-design-system-2.0/molecules/AddressSearchInput';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { ArrowLeft } from '../../assets/icons/ArrowLeft';
import { FormChangeEvent, useFormValidation } from '../../hooks/useFormValidation';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Checkbox } from '@domgen/base-design-system-2.0/atoms/Checkbox';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { store } from '../../store/store';
import { StepKey } from '../../enums/stepKey';
import { formFieldValidators } from './constants/formFieldValidators';
import { PersonalDetailsForm } from './components/PersonalDetailsForm';
import { validateAddressMatch } from '../../utils/address/validateAddressMatch';
import { FormKey } from '../../enums/formKey';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { handleGaPersonalDetailsFormEvents } from '../../utils/ga/handleGaPersonalDetailsFormEvents';
import { usePostHog } from '@posthog/react';
import { AddressForm } from './components/AddressForm';
import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { CircleExclamationIcon } from '../../assets/icons/CircleExclamationIcon';
import { getSystemCode } from '../../utils/getSystemCode';
import { LoadingPage } from '../LoadingPage';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useAppState } from '../../store/selectors/useAppState';
import { useQuote } from '../../store/selectors/useQuote';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { formApplianceName } from '../../utils/store/formApplianceName';
import { formCategoryName } from '../../utils/store/formCategoryName';
import { PageBackgroundWrapper } from '../../components/PageBackgroundWrapper';
import { StoreEvent } from '../../enums/storeEvent';
import { QuoteStatus } from '../../models/store/quoteStatus';

const styles = {
  pageContainer: 'flex justify-center items-center bg-bg-main py-space-700 px-space-500',
  panel:
    'min-h-100 w-2xl h-auto p-space-500 flex flex-col justify-center rounded-radius-100 bg-bg-main-container',
  errorMessage: 'text-sm text-red-600 mt-1',
  navigation: 'flex flex-col w-full gap-space-500 md:flex-row-reverse md:justify-between',
  formGrid: 'grid grid-cols-8 gap-x-space-400 gap-y-space-500 ',
};

type PersonalDetailsPageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function PersonalDetailsPage({ stepNumber, totalSteps }: PersonalDetailsPageProps) {
  const { VITE_CLIENT_FRIENDLY_NAME } = import.meta.env;
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const quote = useQuote();
  const [merchandiseLocatedAtSameAddress, setMerchandiseLocatedAtSameAddress] = useState(true);
  const [displayMerchandiseLocationAddressForm, setDisplayMerchandiseLocationAddressForm] =
    useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);

  const [selectedMerchandiseAddress, setSelectedMerchandiseAddress] =
    useState<AddressSearchResult | null>(null);
  const [selectedSearchAddress, setSelectedSearchAddress] = useState<AddressSearchResult | null>(
    null,
  );
  const posthog = usePostHog();
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const title =
    !merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm
      ? 'Please tell us the address where the appliance is installed'
      : 'Please enter your details';

  const sessionId = session?.id ?? '';

  useEffect(() => {
    gaPageView({
      pagename: 'personal-details',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: sessionId,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
    });
  }, []);
  const { formValues, errors, handleChange, validateAllFields, setFormValues, setErrorsState } =
    useFormValidation(
      {
        firstName: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.FIRST_NAME] ?? '',
        lastName: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.LAST_NAME] ?? '',
        email: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.EMAIL] ?? '',
        phoneNumber: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.PHONE_NUMBER] ?? '',
        phoneDiallingCode:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.PHONE_DIALLING_CODE] ?? '+1',
        countryCode: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.COUNTRY_CODE] ?? 'USA',
        addressLine1: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.ADDRESS_LINE_1] ?? '',
        addressLine2: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.ADDRESS_LINE_2] ?? '',
        city: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.CITY] ?? '',
        regionOrStateCode: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.STATE_CODE] ?? '',
        postalCode: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.POSTAL_CODE] ?? '',
        externalAVSAddressId: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.ADDRESS_ID] ?? '',
        merchandiseLocationLine1:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_ADDRESS_LINE_1] ?? '',
        merchandiseLocationLine2:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_ADDRESS_LINE_2] ?? '',
        merchandiseLocationCity:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_CITY] ?? '',
        merchandiseLocationRegionOrStateCode:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_STATE_CODE] ?? '',
        merchandiseLocationPostalCode:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_POSTAL_CODE] ?? '',
        merchandiseLocationExternalAVSAddressId:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_ADDRESS_ID] ?? '',
      },
      formFieldValidators,
    );

  const progressBarRef = useRef<HTMLDivElement | null>(null);

  const handleAddressSelect = useCallback(
    (address: AddressSearchResult, addressType?: 'mailing' | 'merchandise') => {
      if (addressType === 'mailing') {
        setSelectedSearchAddress(address);
        setFormValues((prev) => ({
          ...prev,
          [FormKey.ADDRESS_LINE_1]: address.addressLine1,
          [FormKey.ADDRESS_LINE_2]: address.addressLine2 ?? '',
          [FormKey.CITY]: address.city,
          [FormKey.STATE_CODE]: address.regionOrStateCode,
          [FormKey.POSTAL_CODE]: address.postalCode,
          [FormKey.ADDRESS_ID]: address.id ?? '',
        }));

        Object.entries(address).forEach(([key, value]) => {
          if (value) {
            handleGaPersonalDetailsFormEvents(
              key,
              'personal-details',
              applianceCategory,
              applianceName,
              sessionId,
              systemCode,
              'Auto',
            );
          }
        });
      }
      if (addressType === 'merchandise') {
        setSelectedMerchandiseAddress(address);
        setFormValues((prev) => ({
          ...prev,
          [FormKey.MERCHANDISE_ADDRESS_LINE_1]: address.addressLine1,
          [FormKey.MERCHANDISE_ADDRESS_LINE_2]: address.addressLine2 ?? '',
          [FormKey.MERCHANDISE_CITY]: address.city,
          [FormKey.MERCHANDISE_STATE_CODE]: address.regionOrStateCode,
          [FormKey.MERCHANDISE_POSTAL_CODE]: address.postalCode,
          [FormKey.MERCHANDISE_ADDRESS_ID]: address.id ?? '',
          [FormKey.COUNTRY_CODE]: 'USA',
        }));

        Object.entries(address).forEach(([key, value]) => {
          if (value) {
            handleGaPersonalDetailsFormEvents(
              key,
              'appliance-location',
              applianceCategory,
              applianceName,
              sessionId,
              systemCode,
              'Auto',
            );
          }
        });
      }
    },

    [setSelectedMerchandiseAddress, setSelectedSearchAddress, setFormValues],
  );

  const handleBack: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      appSessionID: session?.id,
      pagename: displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details',
      regSystemCode: systemCode,
    });

    if (displayMerchandiseLocationAddressForm) {
      setDisplayMerchandiseLocationAddressForm(false);
      capturePostHogEvent(posthog, 'appliance-location-back-clicked', store.get());
    } else {
      store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.PERSONAL_DETAILS } });
      capturePostHogEvent(posthog, 'personal-details-back-clicked', store.get());
    }
  };

  const validatePersonalDetails = () => {
    const personalDetailsFields = [
      'firstName',
      'lastName',
      'addressLine1',
      'city',
      'regionOrStateCode',
      'postalCode',
      'email',
      'phoneNumber',
      'phoneDiallingCode',
    ];
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    personalDetailsFields.forEach((field: string) => {
      const value = formValues[field as keyof typeof formValues];

      const validator = formFieldValidators[field as keyof typeof formFieldValidators]?.find(
        (validation) => typeof validation === 'function',
      ) as (value: string) => string | undefined;

      if (typeof validator === 'function') {
        const error = validator(value);
        if (error) {
          newErrors[field] = error;
          hasErrors = true;
        }
      }
    });
    //Set errors in useFormValidation state
    setErrorsState(newErrors);
    return hasErrors;
  };

  const handleContinue: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const hasErrors = validatePersonalDetails();

    gaEvent({
      pagename: displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: hasErrors || !termsAccepted ? 'failed' : 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      appSessionID: session?.id,
    });

    // If merchandise is NOT at same address AND we haven't shown the form yet, show it
    if (!merchandiseLocatedAtSameAddress && !displayMerchandiseLocationAddressForm) {
      if (hasErrors || !termsAccepted) {
        capturePostHogEvent(posthog, 'personal-details-validation-failed-on-continue', store.get());
        if (!termsAccepted) {
          setTermsError(true);
        }
        return;
      }

      setDisplayMerchandiseLocationAddressForm(true);
      gaPageView({
        pagename: 'appliance-location',
        category: 'registration-pages',
        type: 'STANDARD',
        appSessionID: session?.id,
        regSystemCode: systemCode,
        applianceName,
        applianceCategory,
      });
      capturePostHogEvent(posthog, 'personal-details-continue-cta-clicked', store.get());
      return;
    }

    // Create updated form values if merchandise is at same address
    let currentFormValues = formValues;

    if (merchandiseLocatedAtSameAddress) {
      currentFormValues = {
        ...formValues,
        [FormKey.MERCHANDISE_ADDRESS_LINE_1]: formValues.addressLine1,
        [FormKey.MERCHANDISE_ADDRESS_LINE_2]: formValues.addressLine2,
        [FormKey.MERCHANDISE_CITY]: formValues.city,
        [FormKey.MERCHANDISE_STATE_CODE]: formValues.regionOrStateCode,
        [FormKey.MERCHANDISE_POSTAL_CODE]: formValues.postalCode,
        [FormKey.MERCHANDISE_ADDRESS_ID]: formValues.externalAVSAddressId,
      };
      setFormValues(currentFormValues);
    }
    if (!termsAccepted) {
      setTermsError(true);
    }

    const isValid = validateAllFields(currentFormValues);
    if (!isValid || !termsAccepted) {
      capturePostHogEvent(
        posthog,
        displayMerchandiseLocationAddressForm
          ? 'appliance-location-validation-failed-on-continue'
          : 'personal-details-validation-failed-on-continue',
        store.get(),
      );
      return;
    }

    capturePostHogEvent(
      posthog,
      `${!merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details'}-continue-cta-clicked`,
      store.get(),
    );

    onSubmit(currentFormValues);
  };

  const toggleTermsCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(e.target.checked);
    if (e.target.checked) {
      setTermsError(false);
    }
    gaEvent({
      pagename: displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details',
      eventCategory: 'Terms and Conditions Checkbox',
      eventAction: 'Continue',
      eventLabel: e.target.checked ? 'success' : 'failed',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      appSessionID: session?.id,
    });
    capturePostHogEvent(
      posthog,
      `${!merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details'}-terms-checkbox-changed`,
      store.get(),
    );
  };

  const onSubmit = (valuesToSubmit = formValues) => {
    store.send({
      type: StoreEvent.SUBMIT,
      data: {
        [StepKey.PERSONAL_DETAILS]: {
          [FormKey.FIRST_NAME]: valuesToSubmit.firstName,
          [FormKey.LAST_NAME]: valuesToSubmit.lastName,
          [FormKey.EMAIL]: valuesToSubmit.email,
          [FormKey.PHONE_DIALLING_CODE]: valuesToSubmit.phoneDiallingCode,
          [FormKey.PHONE_NUMBER]: valuesToSubmit.phoneNumber,
          [FormKey.ADDRESS_LINE_1]: valuesToSubmit.addressLine1,
          [FormKey.ADDRESS_LINE_2]: valuesToSubmit.addressLine2,
          [FormKey.POSTAL_CODE]: valuesToSubmit.postalCode,
          [FormKey.STATE_CODE]: valuesToSubmit.regionOrStateCode,
          [FormKey.CITY]: valuesToSubmit.city,
          [FormKey.ADDRESS_IS_FORMATTED]: validateAddressMatch(
            valuesToSubmit,
            selectedSearchAddress,
          ),
          [FormKey.MERCHANDISE_ADDRESS_LINE_1]: valuesToSubmit.merchandiseLocationLine1,
          [FormKey.MERCHANDISE_ADDRESS_LINE_2]: valuesToSubmit.merchandiseLocationLine2,
          [FormKey.MERCHANDISE_CITY]: valuesToSubmit.merchandiseLocationCity,
          [FormKey.MERCHANDISE_POSTAL_CODE]: valuesToSubmit.merchandiseLocationPostalCode,
          [FormKey.MERCHANDISE_STATE_CODE]: valuesToSubmit.merchandiseLocationRegionOrStateCode,
          [FormKey.MERCHANDISE_IS_FORMATTED]: validateAddressMatch(
            valuesToSubmit,
            selectedMerchandiseAddress,
          ),
          [FormKey.COUNTRY_CODE]: 'USA',
        },
      },
    });
  };

  const onBlur = (e: FormChangeEvent, pageName: 'appliance-location' | 'personal-details') => {
    // Note: handleChange is intentionally NOT called here — it is already wired to onChange on
    // each input. Calling it here too triggers a  re-render and second onBlur fire,
    // which causes duplicate GA events in GTM/GA4.

    //Validate the input synchronously to ensure it is valid before firing GA event

    const validators = (
      formFieldValidators as Record<string, ((value: string) => string | undefined)[]>
    )[e.target.name];

    if (!validators) {
      return;
    }

    const hasErrors = validators.some((validator) => {
      const error = validator(e.target.value);
      return error !== undefined && error !== '';
    });

    if (!hasErrors) {
      handleGaPersonalDetailsFormEvents(
        e.target.name,
        pageName,
        applianceCategory,
        applianceName,
        sessionId,
        systemCode,
        'Manual',
      );
    }
  };

  useEffect(() => {
    smoothScrollIntoView(progressBarRef.current);
  }, [displayMerchandiseLocationAddressForm]);

  if (quote?.status === QuoteStatus.LOADING) {
    return <LoadingPage />;
  }

  return (
    <PageBackgroundWrapper>
      <div className="grid grid-cols-1 gap-space-400">
        <ProgressBars ref={progressBarRef} total={totalSteps} current={stepNumber} />
        <h1 className="text-h3">{title}</h1>
        <p>This ensures your appliance is registered to the right person and location.</p>
        <form className={styles.formGrid}>
          {!merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm ? (
            <AddressForm
              formValues={formValues}
              errors={errors}
              onAddressSelect={handleAddressSelect}
              handleFormChange={handleChange}
              onBlur={(e) => onBlur(e, 'appliance-location')}
              addressType="merchandise"
              applianceCategory={applianceCategory}
              sessionId={sessionId}
              systemCode={systemCode}
            />
          ) : (
            <PersonalDetailsForm
              formValues={formValues}
              errors={errors}
              handleChange={handleChange}
              onBlur={(e) => onBlur(e, 'personal-details')}
              onAddressSelect={handleAddressSelect}
              setMerchandiseLocatedAtSameAddress={setMerchandiseLocatedAtSameAddress}
              merchandiseLocatedAtSameAddress={merchandiseLocatedAtSameAddress}
              applianceCategory={applianceCategory}
              sessionId={sessionId}
              systemCode={systemCode}
            />
          )}
        </form>
        {!displayMerchandiseLocationAddressForm && (
          <div className="flex flex-col mt-space-100 mb-space-100">
            <Checkbox
              checked={termsAccepted}
              onChange={toggleTermsCheckbox}
              label={
                <span className="text-body">
                  I agree to the{' '}
                  <a
                    href="https://www.domesticandgeneral.com/us/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body-link"
                  >
                    Terms of Use
                  </a>{' '}
                  and acknowledge the privacy notice of{' '}
                  <a
                    href="https://www.whirlpoolcorp.com/privacy-policy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body-link"
                  >
                    {VITE_CLIENT_FRIENDLY_NAME}{' '}
                  </a>
                  and{' '}
                  <a
                    href="https://www.domesticandgeneral.com/us/security-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body-link"
                  >
                    Domestic and General
                  </a>
                </span>
              }
            />
            {termsError && (
              <div className="inline-flex items-center w-full text-text-error gap-space-100 mt-space-100">
                <CircleExclamationIcon />
                <span className="text-sm">Please accept the terms and conditions to continue</span>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col w-full items-center gap-space-500 md:flex-row-reverse md:justify-between">
          <div className="w-full self-end md:w-fit">
            <Button onClick={handleContinue}>Continue</Button>
          </div>
          <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
            Go Back
          </TextLink>
        </div>
      </div>
    </PageBackgroundWrapper>
  );
}
